import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import _ from "lodash";
import { Breadcrumbs, Button, Header, LoadingState, View, Workspace } from "components/Shared";
import { Input } from "components/Inputs";
import axios from "v2/utils/axios";
import { getAuthTokenFromCookie } from "lib/hooks";
import { LocalNotification } from "lib";

interface Props extends RouteComponentProps {
  match: any;
}

const FROM_ADDRESS_TOOLTIP =
  "Email address that notification email about this property is sent from. The sender name is always " +
  "'TCPinpoint', so this address becomes TCPinpoint <address>. Overrides your company's address; leave " +
  "blank to inherit it. Only used when the company has an SMTP configuration — without one, email is " +
  "sent as the TCPinpoint default.";

/**
 * Property edit page, reached from the Edit button in the property header.
 *
 * Deliberately built from the same pieces as the project edit page
 * (components/Projects/Edit + Projects/Form) — Header/Workspace/View wrapping a
 * section of `Input`s and a bare `Button` — so the two pages look identical. The
 * only difference is the field list: this page holds the SMTP from address alone.
 *
 * Open to company admins and property administrators; PropertySettingsPolicy
 * enforces the same rule server-side, so someone typing the URL gets a 403
 * rather than a usable form.
 */
const EditProperty = ({ match, history }: Props) => {
  const id = match?.params?.id;
  const [property, setProperty] = useState({} as any);
  const [data, setData] = useState({} as any);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const endpoint = `${process.env.REACT_APP_API_ENDPOINT_V2}properties/${id}/settings`;
  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getAuthTokenFromCookie()}`,
  };

  useEffect(() => {
    if (!id) return;

    axios
      .get(endpoint, { headers: authHeaders })
      .then((res) => {
        const attributes = res.data?.data?.attributes || {};
        setProperty(attributes);
        setData({ smtp_from_address: attributes.smtp_from_address || "" });
      })
      .catch((err) => {
        const message =
          _.includes([401, 403], err?.response?.status)
            ? "You do not have permission to edit this property"
            : "Could not load the property settings";
        new LocalNotification(message, message, "error");
        console.error("Error fetching property settings:", err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = () => {
    setSaving(true);
    axios
      .put(endpoint, { property: { smtp_from_address: data.smtp_from_address } }, { headers: authHeaders })
      .then(() => {
        new LocalNotification("Property settings saved!", "tcbot", "success");
        history.push(`/properties/${id}`);
      })
      .catch((err) => {
        // The API rejects anything that is not a bare email address; surface its
        // message on the field the same way the project form surfaces validation.
        const detail = err?.response?.data?.errors?.detail;
        const message = Array.isArray(detail) ? detail.join(", ") : detail || "Something Went Wrong";
        setData(_.assign({}, data, { errors: { smtp_from_address: [message] } }));
        new LocalNotification(message, message, "error");
        console.error("Error saving property settings:", err);
      })
      .finally(() => setSaving(false));
  };

  if (loading) return <LoadingState />;

  return (
    <React.Fragment>
      <Breadcrumbs
        items={[
          { link: "/properties", title: "properties" },
          { link: `/properties/${id}`, title: property.name },
          { title: "edit" },
        ]}
      />
      <Header title={property.name} stamp="property" />
      <Workspace id="property-edit">
        <View>
          <section>
            <Input
              name="smtp_from_address"
              label="SMTP from address"
              tooltip={FROM_ADDRESS_TOOLTIP}
              type="text"
              placeholder="e.g. notifications@example.com.au"
              defaultState={data}
              onChange={(smtp_from_address: string) => setData(_.assign({}, data, { smtp_from_address }))}
            />
            <Button disabled={saving} onClick={onSubmit} />
          </section>
        </View>
      </Workspace>
    </React.Fragment>
  );
};

export default EditProperty;
